/**
 * 解説 as the blocks the book lays out (日本-524).
 *
 * **The problem this replaces is a measurement, not a preference.** 解説 rendered as one
 * `white-space: pre-line` paragraph at `text-meta` — 14px, the type ramp's FLOOR — at full bleed:
 * a **1003px measure at 1147 and 1456px at 1600**, which is 150 and 210 characters a line. It
 * averages 1,549 characters and reaches 9,646, and every line of it was one style, so a numbered
 * note, its examples, their translations and a 【Related Expressions】 essay were typographically
 * indistinguishable.
 *
 * **The structure is really in the data.** Measured over the 628 entries that carry 解説:
 *
 * | shape | lines |
 * |---|---|
 * | Latin prose or a translation | 3,008 |
 * | `a.` / `b.` sub-example | 1,931 |
 * | Japanese or mixed | 1,861 |
 * | `N.` numbered note | 1,624 |
 * | `(n)` example, marker and text on one line | 1,151 |
 * | `[n]` related-expression example, ditto | 1,024 |
 * | `(⇨ …)` cross-reference alone on its line | 494 |
 * | `【…】` header | 436 |
 * | roman `I.` / `II.` section | 384 |
 * | `[n]` bare, its sentence on the next line | 233 |
 * | `(n)` bare | 5 |
 *
 * **It fails open.** Anything unrecognised is `prose`, which renders as a paragraph and loses
 * nothing — `grammarPos.ts`'s rule and its exact reason: this is *stored data*, extracted from the
 * deck, and a future re-extraction may reshape any of it. The corpus test asserts the strong form
 * of that promise: **every non-blank input line appears in exactly one output block**, so a shape
 * this parser has never seen degrades to a paragraph rather than vanishing.
 */

/** `1.` at the head of a line. */
const NOTE = /^(\d+)\.\s+/;

/**
 * `2.` and `3.` jammed mid-line, which the extraction really did to three entries.
 *
 * basic-010 line 4 reads `…For animate things, いる1 is used. 2. The negative informal form of ある
 * is not あらない but ない. 3. Two sentence patterns…` — two whole notes inside one line. Without
 * this they are swallowed by note 1's body, which is the wall this ticket exists to remove, in
 * miniature.
 *
 * **Three constraints together, and none of them is spare.** The number must be the SUCCESSOR of
 * the note currently open, it must be preceded by sentence-ending punctuation, and it must be
 * followed by a space. Splitting on a bare `\d+\.` mid-sentence would cut `as in Ex. 2. The…` and
 * every version number in the book. Measured over all 628: this fires **9 times across 3 entries —
 * basic-010 (+2), basic-014 (+4), basic-172 (+3) — and every one is a genuine note boundary**,
 * checked by reading each in context. So it is a narrow repair of a known extraction defect rather
 * than a general rule, and a tenth appearing after a re-extraction is worth reading before it is
 * trusted.
 */
const JAMMED_NOTE = /(?<=[.。])\s(\d+)\.\s/gu;

/** `I.` / `II.` — the sections inside 【Related Expressions】. Drawn like a note, with its own figure. */
const ROMAN = /^([IVX]{1,4})\.\s+/;

/**
 * `(1)` `(1).` `[1]` `a.` — an example's marker, with or without its sentence on the same line.
 *
 * **A bracketed marker is unambiguous and a bare letter is not.** `(1)` and `[1]` can only be the
 * book numbering an example, so they are taken wherever they appear — including alone on their
 * line, which is how 233 of them are written. A lone `a.` is a different matter: English prose
 * begins `e.g.` and `i.e.`, and reading those as markers would cut a sentence in half. So a letter
 * marker is taken only when what follows it is not English — which is every real sub-example,
 * because a sub-example is a Japanese sentence.
 *
 * **The letter must carry its period.** Without it `[a-z]` matched the first letter of any word,
 * and basic-080's gloss `see 〜 in advance` came out as marker `s.` plus `ee 〜 in advance` — a
 * character INSERTED into the book's text, which is the one thing this parser must not do. It got
 * that far because `〜` is not in `LATIN_ONLY`, so the line did not read as English. Found by the
 * no-character-lost assertion.
 *
 * **The marker keeps whatever punctuation the book gave it**, `(2)` and `(2).` alike. Normalising
 * here would drop a character, and the corpus test's promise is that this parser never does — so
 * the tidying belongs to the renderer (`markerLabel` in `GrammarBlocks.tsx`), which is allowed to
 * present what the parser is not allowed to discard.
 */
const EXAMPLE = /^(\(\d+\)\.?|\[\d+\]|[a-z]\.)\s*/;
const BRACKETED = /^[([]/;

/**
 * `【Related Expressions】`, `【Related Expression: ながら; うちに】` — and the square-bracket spelling.
 *
 * **The book is inconsistent about which brackets it uses.** 436 lines are 【】 and a handful are
 * `[Related Expression]`; basic-116 is one of the latter, and matching only 【】 left it rendering
 * as an ordinary paragraph in the middle of the commentary — the one place 解説 changes subject,
 * drawn as though it had not. The `[…]` form is admitted only when it says *Related*, so it cannot
 * swallow an example marker.
 */
const HEADER = /^(?:【[^】]*】|\[[^\]]*Related[^\]]*\])\s*$/i;

/** `(⇨ で1; に6)` alone on its line. */
const CROSS_REF = /^\(⇨\s*([^)]*)\)\s*$/;

/**
 * Latin-only, which is what tells a translation from another Japanese line.
 *
 * The book's glosses carry `*` and `??` grammaticality marks and curly quotes, so the class is
 * wider than plain ASCII. **A Japanese line followed by a Latin one is a sentence and its
 * translation 2,605 times out of 2,953 (88%)**, which is what lets the gloss set quieter and
 * smaller instead of as a second paragraph.
 */
const LATIN_ONLY = /^[\x20-\x7e‘-”–—… ]+$/;

/**
 * A line the book drew as a diagram in monospace — 13 of them, across 10 entries.
 *
 * basic-116 is one, which matters because it is the entry Alex reported: `間に -------Event 1-------`
 * is a timeline, and proportional type with collapsed whitespace destroys it.
 *
 * **A flag rather than a block kind of its own.** Six of the thirteen sit *inside* an example
 * (basic-018's `↑____________________↑` under its `(1) a.`, basic-173's `⎧a. ⎨b. ⎪c. ⎩d.` brace
 * column), and lifting the art out of them would mean splitting a line on a guess. Marking the
 * block preformatted keeps every character where the book put it and needs no structural claim at
 * all — but it does mean the flag has to be read off the SENTENCE, since a borrowed one is not on
 * the line its marker was found on.
 */
const DIAGRAM = /[─━―\-=_]{6,}|[│|┃⎪].*[│|┃⎪]/;

/**
 * Is this line one the book uses structurally?
 *
 * **Nothing structural may be swallowed as a translation.** The gloss lookahead takes the next
 * Latin-only line, and `2. The negative informal form…` is Latin-only — so without this a numbered
 * note following a Japanese sentence was eaten as that sentence's English, and vanished from the
 * output. Found by the corpus test, which counted three fewer notes than the source has `N.` lines.
 */
function structural(text: string): boolean {
  if (HEADER.test(text) || CROSS_REF.test(text)) return true;
  if (NOTE.test(text) || ROMAN.test(text)) return true;
  return BRACKETED.test(text) && EXAMPLE.test(text);
}

export type NotesBlock =
  /** A numbered note or a roman-numeral section: `marker` hangs in the margin. */
  | { kind: "note"; marker: string; body: string; preformatted: boolean }
  /** A sentence from the book, with its translation where it has one. */
  | { kind: "example"; marker: string | null; ja: string; en: string | null; preformatted: boolean }
  /** `【Related Expressions】` and friends — the one place 解説 changes subject. */
  | { kind: "header"; text: string }
  /** `(⇨ で1; に6)` → chips. `text` is the book's own inner string, separators and all. */
  | { kind: "crossRef"; text: string; targets: string[] }
  /** Everything else, including the continuation of the note above it. */
  | { kind: "prose"; text: string; preformatted: boolean };

interface Line {
  text: string;
  blank: boolean;
}

/**
 * Split a line at a note jammed inside it, or answer null.
 *
 * The successor constraint is checked HERE rather than at the call sites, so there is one place
 * that decides what a jammed note is — see `JAMMED_NOTE` for why all three constraints are needed.
 * The tail keeps its own `N.`, so it is re-read as an ordinary note line and can be split again:
 * basic-010 carries notes 2 AND 3 inside one line and both come back out.
 */
function splitJammed(text: string, openNote: number): [head: string, tail: string] | null {
  // **Every candidate in the line is tried, not just the first.** basic-018 and basic-023 carry a
  // `. 2.` that is a citation before the `. 3.` that is a real boundary, so testing only the
  // leading match bailed on the line and lost the note. A global regex needs its `lastIndex`
  // reset, or the next call resumes mid-string and answers about a different line.
  JAMMED_NOTE.lastIndex = 0;
  for (let match = JAMMED_NOTE.exec(text); match; match = JAMMED_NOTE.exec(text)) {
    if (Number(match[1]) !== openNote + 1) continue;
    JAMMED_NOTE.lastIndex = 0;
    return [text.slice(0, match.index), text.slice(match.index + 1)];
  }
  return null;
}

function lines(notes: string): Line[] {
  return notes.split("\n").map((line) => {
    const text = line.trim();
    return { text, blank: text.length === 0 };
  });
}

/**
 * The blocks 解説 is made of, in the book's order.
 *
 * **Examples are assembled by looking AHEAD, not by classifying each line alone.** A marker is
 * bare on its own line 238 times, and the translation is always the line after the sentence — so a
 * line-at-a-time classifier would emit three blocks where the book has one example, and the gloss
 * would lose the sentence it belongs to.
 */
export function notesBlocks(notes: string): NotesBlock[] {
  const source = lines(notes);
  const blocks: NotesBlock[] = [];
  let openNote = 0;

  const pre = (text: string) => DIAGRAM.test(text);

  for (let i = 0; i < source.length; i += 1) {
    const line = source[i] as Line;
    if (line.blank) continue;
    let text = line.text;

    const crossRef = CROSS_REF.exec(text);
    if (crossRef) {
      // **Split on `;` alone, not on a comma.** `(⇨ くれる1, Related expression II)` is ONE
      // reference with a qualifier; splitting on the comma turns `Related expression II` into a
      // chip pointing at nothing. The semicolon is the book's own separator between references.
      const text = crossRef[1] ?? "";
      const targets = text
        .split(/[;；]/)
        .map((target) => target.trim())
        .filter((target) => target.length > 0);
      blocks.push({ kind: "crossRef", text, targets });
      continue;
    }

    if (HEADER.test(text)) {
      blocks.push({ kind: "header", text });
      // A 【Related Expressions】 section restarts the numbering as roman figures, so an `N.`
      // appearing after it is a new note rather than a successor to the one before the header.
      openNote = 0;
      continue;
    }

    const note = NOTE.exec(text);
    if (note) {
      openNote = Number(note[1]);
      let body = text.slice(note[0].length);
      // The body may itself carry the NEXT note. Re-reading the tail as a line rather than
      // recursing is what lets one line yield notes 2 and 3 — basic-010 does exactly that.
      const split = splitJammed(body, openNote);
      if (split) {
        body = split[0];
        source[i] = { text: split[1].trim(), blank: false };
        i -= 1;
      }
      blocks.push({
        kind: "note",
        marker: `${note[1]}.`,
        body: body.trim(),
        preformatted: pre(text),
      });
      continue;
    }

    const roman = ROMAN.exec(text);
    if (roman) {
      blocks.push({
        kind: "note",
        marker: `${roman[1]}.`,
        body: text.slice(roman[0].length),
        preformatted: pre(text),
      });
      continue;
    }

    const example = EXAMPLE.exec(text);
    if (example && (BRACKETED.test(text) || !LATIN_ONLY.test(text))) {
      const marker = example[1] as string;
      // **Nothing is consumed until the example is committed.** A bare marker's sentence is on the
      // next line (233 examples are written that way), but the branch can still bail — so the
      // lookahead is resolved first and `i` advances only once the block is actually pushed.
      // Advancing eagerly and then falling through dropped the consumed line outright.
      const inline = text.slice(example[0].length).trim();
      const next = source[i + 1];
      const borrowed = !inline && next && !next.blank && !structural(next.text) ? next.text : null;
      const ja = inline || borrowed || "";

      // **A bracketed marker with an ENGLISH sentence is a citation, not an example.** basic-116
      // ends `[1] and [2] focus on 'duration of some action/state'…` — prose that happens to open
      // with the number of an example it is discussing. Read as an example it set English in the
      // Japanese face and orphaned the paragraph from the argument it belongs to. Every real
      // example in the book is a Japanese sentence, so the sentence itself is the test.
      if (ja && !LATIN_ONLY.test(ja)) {
        if (borrowed) i += 1;
        const after = source[i + 1];
        let en: string | null = null;
        if (after && !after.blank && LATIN_ONLY.test(after.text) && !structural(after.text)) {
          en = after.text;
          i += 1;
        }
        blocks.push({
          kind: "example",
          marker,
          ja,
          en,
          // **Tested on the SENTENCE, never on the line the marker was found on.** A marker is
          // bare on its own line 233 times, so `ja` is routinely borrowed from the line below —
          // and `text` is then just `(1)`, which carries no diagram however much art the sentence
          // holds. Measured over the corpus: basic-173's `⎧a. ⎨b. ⎪c. ⎩d.` brace column and
          // intermediate-207's `↑ … |` scale are both borrowed lines, and both were set
          // proportional with their whitespace collapsed — the exact loss this flag exists to
          // prevent. Where the sentence IS inline, `ja` is a substring of `text` and no marker can
          // contribute a match, so this is strictly the better test in both branches.
          preformatted: pre(ja) || (en !== null && pre(en)),
        });
        continue;
      }
      // Not an example after all — fall through and keep the line as written.
    }

    // An unmarked Japanese line followed by a Latin one is a sentence and its translation — the
    // book writes plenty of them with no figure at all, and losing the pairing here would put the
    // gloss in its own paragraph as though it were commentary.
    const after = source[i + 1];
    if (
      !LATIN_ONLY.test(text) &&
      after &&
      !after.blank &&
      LATIN_ONLY.test(after.text) &&
      !structural(after.text)
    ) {
      blocks.push({
        kind: "example",
        marker: null,
        ja: text,
        en: after.text,
        preformatted: pre(text) || pre(after.text),
      });
      i += 1;
      continue;
    }

    // A note jammed into the tail of the prose above it — see JAMMED_NOTE.
    const jammed = splitJammed(text, openNote);
    if (jammed) {
      const [head, tail] = jammed;
      if (head.trim()) blocks.push({ kind: "prose", text: head.trim(), preformatted: pre(head) });
      source[i] = { text: tail.trim(), blank: false };
      i -= 1;
      continue;
    }

    blocks.push({ kind: "prose", text, preformatted: pre(text) });
  }

  return blocks;
}

/**
 * Where the 【Related Expressions】 section begins, or -1.
 *
 * The layout sets that half apart — it stops being commentary on this entry and becomes a
 * comparison with other ones — and the header is the only marker the book gives for it. Returned
 * as an index rather than by splitting the array, so the caller keeps one list and the block
 * indices stay stable.
 */
export function relatedFrom(blocks: readonly NotesBlock[]): number {
  return blocks.findIndex((block) => block.kind === "header" && /Related/i.test(block.text));
}
